import React, {
  useCallback,
  useEffect,
  useState,
  useRef,
  useMemo,
} from "react";
import NewsDetail from "../components/NewsDetail/NewsDetail";
import { useSearchParams, useNavigate } from "react-router-dom";
import * as apis from "../apis";
import useTabs from "../hooks/useTabs";
import dayjs from "dayjs";

export default function NewsDetailContainer() {
  const navigate = useNavigate();

  const [searchParams] = useSearchParams();
  const id = searchParams.get("id");
  const [previewImage, setPreviewImage] = useState(null);

  const imageInputRef = useRef(null);
  const editorRef = useRef(null);

  const tabButtons = useMemo(
    () => [
      { id: 1, tab: "한국어", locale: "ko" },
      { id: 2, tab: "영문", locale: "en" },
    ],
    []
  );

  const { currentItem, changeItem } = useTabs(0, tabButtons);

  const [data, setData] = useState({
    content: "",
    title: "",
    images: [],
    link: "",
    date: dayjs(new Date()).format("YYYY-MM-DD"),
    locale: currentItem.locale,
  });
  const [isDataLoaded, setIsDataLoaded] = useState(false);
  const [disabledLocales, setDisabledLocales] = useState([]);

  useEffect(() => {
    if (id) {
      const fetchData = async () => {
        try {
          const res = await apis.getNewsOne(id);
          const result = res?.data?.data;
          setData(result);
          setIsDataLoaded(true);

          // ✅ locale에 맞는 탭 자동 활성화
          const matchedIndex = tabButtons.findIndex(
            (tab) => tab.locale === result?.locale
          );
          if (matchedIndex !== -1) {
            changeItem(matchedIndex);
          }

          // ✅ 수정 모드일 때 다른 locale 탭 비활성화 처리
          const disabled = tabButtons
            .filter((tab) => tab.locale !== result?.locale)
            .map((tab) => tab.locale);
          setDisabledLocales(disabled);
        } catch (error) {
          console.error(error);
          alert(
            error.response?.data?.message || "소식을 불러오는데 실패하였습니다."
          );
        }
      };
      fetchData();
    } else {
      // ✅ 등록 모드일 때도 기본적으로 렌더링할 수 있도록 설정
      setIsDataLoaded(true);
    }
  }, [id]);

  useEffect(() => {
    if (id) return; // 수정 모드면 탭 잠금
    // 값이 다를 때만 setState (불필요한 렌더/루프 방지)
    if (data.locale !== currentItem.locale) {
      setData((prev) => ({ ...prev, locale: currentItem.locale }));
    }
    // currentItem 전체 대신 locale만 의존 (참조변경으로 인한 불필요한 실행 방지)
  }, [currentItem.locale, id, data.locale]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setData((prev) => ({ ...prev, [name]: value }));
  };

  const handleChangeImage = useCallback(
    async (e) => {
      const { name, files } = e.target;

      // 현재 이미지 개수 확인
      const currentCount = data[name]?.length || 0;
      const newCount = files.length;

      // ✅ 총합이 5개를 초과하면 업로드 제한
      if (currentCount + newCount > 1) {
        alert("썸네일 이미지는 최대 1개까지만 업로드할 수 있습니다.");
        e.target.value = ""; // input 초기화
        return;
      }

      const imageFormData = new FormData();
      [].forEach.call(files, (file) => {
        imageFormData.append("image", file);
      });

      try {
        const res = await apis.postUpload(imageFormData);

        if (res?.data?.url) {
          setData((prev) => ({
            ...prev,
            [name]: [...(prev[name] || []), res.data.url],
          }));
        }
      } catch (error) {
        console.error("이미지 업로드 실패:", error);
        alert("이미지 업로드 중 오류가 발생했습니다.");
      } finally {
        e.target.value = ""; // ✅ 동일 파일 재선택 가능하도록 초기화
      }
    },
    [data]
  );

  const handleCustomImageUpload = () => {
    imageInputRef.current.click();
  };

  // ✅ 이미지 삭제 핸들러
  const handleRemoveImage = (index) => {
    setData((prev) => ({
      ...prev,
      images: prev.images.filter((_, i) => i !== index),
    }));
  };

  // ✅ 이미지 클릭 시 미리보기
  const handlePreview = (imageUrl) => {
    if (!imageUrl) {
      // 닫기 시 null 전달
      setPreviewImage(null);
    } else {
      setPreviewImage(imageUrl);
    }
  };

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === "Escape") setPreviewImage(null);
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, []);

  const handleEditorChange = (name, value) => {
    setData((prev) => ({ ...prev, [name]: value }));
  };

  // Editor 이미지 업로드
  const handleEditorImageUpload = useCallback(async (blob, callback) => {
    try {
      const formData = new FormData();
      formData.append("image", blob); // 업로드할 이미지 파일

      const response = await apis.postUpload(formData);

      const imageUrl = response?.data?.url; // 반환된 이미지 URL

      callback(imageUrl, "uploaded-image"); // 에디터에 이미지 삽입
    } catch (error) {
      console.error("이미지 업로드 실패:", error);
      alert(error.response?.data?.message || "이미지 업로드에 실패했습니다.");
    }
  }, []);

  const handleSubmit = useCallback(async () => {
    if (data?.title === "") {
      alert("제목을 입력해 주세요.");
      return;
    }

    if (data?.date === "") {
      alert("날짜를 입력해 주세요.");
      return;
    }

    if (data?.content === "") {
      alert("내용을 입력해 주세요.");
      return;
    }

    try {
      if (window.confirm("소식을 추가 하시겠습니까?")) {
        await apis.createNews(data);
        alert("소식이 추가 되었습니다.");
        navigate("/news");
      }
    } catch (error) {
      console.error(error);
      alert(error.response?.data?.message || "소식을 수정하는데 실패했습니다.");
    }
  }, [data, navigate]);

  const handleEdit = useCallback(async () => {
    try {
      if (window.confirm("소식을 수정하시겠습니까?")) {
        await apis.updateNews(id, data);
        alert("소식이 수정되었습니다.");
        navigate("/news");
      }
    } catch (error) {
      console.error(error);
      alert(error.response?.data?.message || "소식을 수정하는데 실패했습니다.");
    }
  }, [data, id, navigate]);

  return (
    <>
      {isDataLoaded && (
        <NewsDetail
          id={id}
          data={data}
          tabButtons={tabButtons}
          currentItem={currentItem}
          changeItem={changeItem}
          disabledLocales={disabledLocales}
          handleChange={handleChange}
          handleChangeImage={handleChangeImage}
          handleCustomImageUpload={handleCustomImageUpload}
          imageInputRef={imageInputRef}
          handleRemoveImage={handleRemoveImage}
          handlePreview={handlePreview}
          previewImage={previewImage}
          editorRef={editorRef}
          handleEditorChange={handleEditorChange}
          handleEditorImageUpload={handleEditorImageUpload}
          handleSubmit={handleSubmit}
          handleEdit={handleEdit}
        />
      )}
    </>
  );
}
